import {
  Archive,
  Blocks,
  BookOpen,
  ChevronRight,
  FlaskConical,
  type LucideIcon,
  Palette,
  Sparkles,
  SquareArrowOutUpRight,
} from 'lucide-react'
import { Fragment, type ReactNode, useId } from 'react'

import { SIDEBAR_ROW, SidebarSeparator } from '@/components/sidebar'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

interface AnchorEntry {
  id: string
  label: string
}

interface ExternalEntry {
  href: string
  label: string
  /** MM/DD/YYYY of the prototype's last change; bump it when you change the prototype. */
  updated?: string
}

const BRAND: AnchorEntry[] = [
  { id: 'logo', label: 'Logo' },
  { id: 'color', label: 'Color' },
  { id: 'type', label: 'Type' },
  { id: 'spacing', label: 'Spacing' },
  { id: 'icons', label: 'Icons' },
]

interface AnchorGroup {
  label?: string
  entries: AnchorEntry[]
}

/** Order must match the render order in DesignSystem.tsx. */
const COMPONENT_GROUPS: AnchorGroup[] = [
  {
    entries: [
      { id: 'buttons', label: 'Buttons' },
      { id: 'links', label: 'Links' },
      { id: 'banners', label: 'Banners' },
      { id: 'toast', label: 'Toast' },
      { id: 'navigation', label: 'Navigation' },
      { id: 'broker-actions', label: 'Broker actions' },
      { id: 'footer', label: 'Footer' },
      { id: 'value-props', label: 'Value props' },
      { id: 'loading', label: 'Loading' },
      { id: 'tooltip', label: 'Tooltip' },
      { id: 'popover', label: 'Popover' },
      { id: 'stepper', label: 'Stepper' },
      { id: 'chat', label: 'Chat' },
      { id: 'skeletons', label: 'Skeletons' },
      { id: 'avatars', label: 'Avatars' },
      { id: 'tool-calls', label: 'Agent tool calls' },
      { id: 'agent', label: 'Agent' },
      { id: 'badge', label: 'Badge' },
      { id: 'file-chip', label: 'File chip' },
      { id: 'idea-card', label: 'Idea card' },
      { id: 'timeline', label: 'Timeline' },
      { id: 'actions-sheet', label: 'Actions sheet' },
    ],
  },
  {
    label: 'Inputs',
    entries: [
      { id: 'fields', label: 'Form field' },
      { id: 'multiline-field', label: 'Multiline form field' },
      { id: 'searchable-select', label: 'Searchable select' },
      { id: 'combobox', label: 'Combobox' },
      { id: 'tag-input', label: 'Tag input' },
      { id: 'slider', label: 'Slider' },
      { id: 'form', label: 'Form' },
      { id: 'card-checkbox', label: 'Card checkbox' },
      { id: 'card-radio', label: 'Card radio' },
      { id: 'segmented-control', label: 'Segmented control' },
    ],
  },
  {
    label: 'Cards',
    entries: [
      { id: 'cards', label: 'Cards' },
      { id: 'carrier-card', label: 'Estimate card' },
      { id: 'quote-card', label: 'Quote card' },
    ],
  },
  {
    label: 'Tables',
    entries: [
      { id: 'table', label: 'Table' },
      { id: 'pagination', label: 'Table pagination' },
      { id: 'table-search', label: 'Table search' },
    ],
  },
]

const TESTING: ExternalEntry[] = [
  { href: '/design-system/prototypes/quote-card', label: 'Quote card' },
  { href: '/design-system/conversation-tree', label: 'Conversation tree' },
]

const PROTOTYPES: ExternalEntry[] = [
  { href: '/design-system/prototypes/claims-v1-0', label: 'Claims V1.0', updated: '10/19/2026' },
  {
    href: '/design-system/prototypes/checkout-manager-v2',
    label: 'Checkout manager V2',
    updated: '10/23/2026',
  },
  {
    href: '/design-system/prototypes/ai-positioning-v1-0',
    label: 'AI positioning V1.0',
    updated: '10/19/2026',
  },
  { href: '/design-system/prototypes/cai-mvp-v1-0', label: 'Cai MVP V1.0', updated: '10/19/2026' },
]

const ARCHIVE: ExternalEntry[] = [
  { href: '/design-system/prototypes/quotes-page', label: 'Quotes page' },
  {
    href: '/design-system/prototypes/confirm-info-cards-v1-0',
    label: 'Confirm info cards V1.0',
  },
  {
    href: '/design-system/prototypes/account-banners-v1-0',
    label: 'Account banners V1.0',
  },
  {
    href: '/design-system/prototypes/account-side-nav-v1-0',
    label: 'Account side nav V1.0',
  },
]

/** Order must match the card order in SkillsSection.tsx. */
const SKILLS: ExternalEntry[] = [
  { href: '#skill-cai-design-review', label: '/cai-design-review' },
  { href: '#skill-cai-prototype', label: '/cai-prototype' },
  { href: '#skill-cai-add-to-design-system', label: '/cai-add-to-design-system' },
  { href: '#skill-cai-language-review', label: '/cai-language-review' },
]

/** A sub-row under an expanded group, on the account sidebar's sub-menu rail. */
export const SUB_ROW =
  'flex h-7 min-w-0 items-center gap-2 overflow-hidden rounded-md px-2 text-sm text-neutral-600 outline-hidden transition-colors hover:bg-ink-50 hover:text-ink focus-visible:ring-2 [&>span:last-child]:truncate'

export function NavGroup({
  label,
  separator,
  children,
}: {
  label?: string
  separator?: boolean
  children: ReactNode
}) {
  const labelId = useId()

  return (
    <>
      {separator && <SidebarSeparator />}
      <div className="flex w-full min-w-0 flex-col px-2 py-3">
        {label && (
          <div
            id={labelId}
            className="flex h-5 shrink-0 items-center truncate px-2 pb-1 text-xs font-medium text-neutral-600/70"
          >
            {label}
          </div>
        )}
        <ul
          className="flex w-full min-w-0 flex-col gap-0.5 text-sm"
          aria-labelledby={label ? labelId : undefined}
        >
          {children}
        </ul>
      </div>
    </>
  )
}

function NavRow({
  href,
  icon: Icon,
  label,
  external,
  updated,
}: {
  href: string
  icon: LucideIcon
  label: string
  external?: boolean
  updated?: string
}) {
  return (
    <li>
      <a
        href={href}
        className={cn(SIDEBAR_ROW, updated && 'h-auto items-start')}
        {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
      >
        {/* A new-tab row trails its icon, like a link out. */}
        {external ? (
          <>
            <span className="flex min-w-0 flex-col items-start gap-0.5">
              <span className="max-w-full truncate">{label}</span>
              {updated && <span className="text-xs text-neutral-400">Last updated {updated}</span>}
            </span>
            <Icon aria-hidden="true" className="ml-auto" />
          </>
        ) : (
          <>
            <Icon aria-hidden="true" />
            <span>{label}</span>
          </>
        )}
      </a>
    </li>
  )
}

interface SubEntry {
  href: string
  label: string
  external?: boolean
}

interface SubGroup {
  label?: string
  entries: SubEntry[]
}

function SubRows({ entries }: { entries: SubEntry[] }) {
  return entries.map((entry) => (
    <li key={entry.href}>
      <a
        href={entry.href}
        className={SUB_ROW}
        {...(entry.external && { target: '_blank', rel: 'noopener noreferrer' })}
      >
        <span>{entry.external ? `${entry.label} ↗` : entry.label}</span>
      </a>
    </li>
  ))
}

function CollapsibleNavRow({
  icon: Icon,
  label,
  groups,
}: {
  icon: LucideIcon
  label: string
  groups: SubGroup[]
}) {
  return (
    <Collapsible className="group/nav-menu" asChild>
      <li>
        <CollapsibleTrigger className={cn(SIDEBAR_ROW, 'cursor-pointer')}>
          <Icon aria-hidden="true" />
          <span>{label}</span>
          <ChevronRight className="ml-auto size-3! text-neutral-400 transition-transform duration-200 group-data-[state=open]/nav-menu:rotate-90" />
        </CollapsibleTrigger>
        <CollapsibleContent asChild>
          <ul className="border-ink-200 mx-3.5 flex min-w-0 flex-col gap-1 border-l px-2.5 py-0.5">
            {groups.map((group, index) => (
              <Fragment key={group.label ?? index}>
                {index > 0 && <li aria-hidden="true" className="bg-ink-200 my-2 h-px shrink-0" />}
                {group.label && (
                  <li className="flex h-5 shrink-0 items-center truncate px-2 pb-1 text-xs font-medium text-neutral-600/70">
                    {group.label}
                  </li>
                )}
                <SubRows entries={group.entries} />
              </Fragment>
            ))}
          </ul>
        </CollapsibleContent>
      </li>
    </Collapsible>
  )
}

const anchors = (entries: AnchorEntry[]): SubEntry[] =>
  entries.map((entry) => ({ href: `#${entry.id}`, label: entry.label }))

const externals = (entries: ExternalEntry[]): SubEntry[] =>
  entries.map((entry) => ({ ...entry, external: true }))

/** Sidebar section index, drawn like the customer account sidebar's expanded state. */
export function DesignSystemNav() {
  return (
    <nav aria-label="Design System" className="flex flex-col">
      <NavGroup>
        <NavRow href="#intro" icon={BookOpen} label="Intro" />
      </NavGroup>
      <NavGroup separator>
        <CollapsibleNavRow icon={Palette} label="Brand" groups={[{ entries: anchors(BRAND) }]} />
        <CollapsibleNavRow icon={Sparkles} label="Skills" groups={[{ entries: SKILLS }]} />
        <CollapsibleNavRow
          icon={Blocks}
          label="Components"
          groups={COMPONENT_GROUPS.map((group) => ({ label: group.label, entries: anchors(group.entries) }))}
        />
        <CollapsibleNavRow icon={FlaskConical} label="Testing" groups={[{ entries: externals(TESTING) }]} />
      </NavGroup>
      <NavGroup label="Prototypes" separator>
        {PROTOTYPES.map((entry) => (
          <NavRow
            key={entry.href}
            href={entry.href}
            icon={SquareArrowOutUpRight}
            label={entry.label}
            updated={entry.updated}
            external
          />
        ))}
      </NavGroup>
      <NavGroup separator>
        <CollapsibleNavRow icon={Archive} label="Archive" groups={[{ entries: externals(ARCHIVE) }]} />
      </NavGroup>
    </nav>
  )
}
